import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "359",
  "slug": "why-some-borders-are-perfectly-straight",
  "title": "Why Are Some Borders Perfectly Straight?",
  "excerpt": "Rivers and mountains make wiggly borders. So why do so many run dead straight across deserts, plains, and even cities? The answer is part geometry, part history, and part colonial map-drawing.",
  "date": "2026-06-05",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1666198045509-1917e86f61a4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBkZXNlcnQlMjBsYW5kc2NhcGV8ZW58MXwwfHx8MTc4MDY0NzE0NXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Straight borders usually exist where there was no obvious natural feature to follow, so people drew lines along latitude and longitude or simple surveyed paths instead. Many of the longest straight borders date from colonial-era treaties that divided land on a map rather than on the ground.",
  "content": [
    {
      "type": "paragraph",
      "text": "Look at a world map and a strange pattern jumps out. Some borders wander and wiggle, hugging rivers and ridgelines. Others run dead straight for hundreds or even thousands of kilometers, slicing across deserts and plains as if drawn with a ruler — which, in a sense, they were. The contrast between these two kinds of border tells you a lot about how, and by whom, a piece of the world was divided."
    },
    {
      "type": "heading",
      "text": "Natural Borders Follow the Land"
    },
    {
      "type": "paragraph",
      "text": "For most of history, borders followed whatever was hardest to cross or easiest to agree on: a river, a mountain crest, a coastline, the edge of a lake. These features are obvious, visible on the ground, and useful as defenses, so they made natural dividing lines. That is why so many borders are squiggly — they are tracing a meandering river or a crumpled mountain range. The Rio Grande between the United States and Mexico, the Pyrenees between France and Spain, and the Rhine along parts of central Europe are all borders that follow nature's lines."
    },
    {
      "type": "heading",
      "text": "Straight Borders Follow Math"
    },
    {
      "type": "paragraph",
      "text": "Straight borders appear where there is no convenient natural feature to follow — or where the people drawing the line ignored the features that were there. Instead of a river, they used the invisible grid of latitude and longitude. The long straight section of the border between the United States and Canada follows the 49th parallel of latitude across the western half of the continent. The border between Egypt and Sudan runs largely along the 22nd parallel. Much of the boundary between Western Australia and its neighbors follows a meridian of longitude."
    },
    {
      "type": "paragraph",
      "text": "These lines are perfectly straight for the simple reason that lines of latitude and longitude are mathematical, not physical. When negotiators had a featureless desert or empty plain to divide, picking a parallel or meridian was the fastest, least disputable way to do it."
    },
    {
      "type": "heading",
      "text": "Empty Land Invites a Ruler"
    },
    {
      "type": "paragraph",
      "text": "Straight borders cluster in two kinds of terrain: deserts and sparsely populated interiors. Across the Sahara, the Arabian Peninsula, the Australian outback, and the American West, you find long ruler-straight boundaries — because where almost nobody lived and no river ran, there was nothing on the ground to argue about. A line on a map was good enough, and surveying a straight line was far cheaper than mapping every contour."
    },
    {
      "type": "heading",
      "text": "The Colonial Map-Drawing Legacy"
    },
    {
      "type": "paragraph",
      "text": "The most consequential straight borders were drawn far from the land they divided. During the colonial era, European powers carved up much of Africa and the Middle East in distant negotiating rooms, frequently settling disputes by ruling straight lines across maps with little regard for the peoples, languages, or natural features on the ground. That is why a huge share of Africa's borders are geometric — long straight segments meeting at sharp angles — rather than following rivers and ethnic boundaries."
    },
    {
      "type": "paragraph",
      "text": "These borders are visually tidy and politically messy. A line drawn for the convenience of a 19th-century treaty could cut a single community in two, lump rival groups together, or hand a resource to one side by accident. Many modern disputes trace directly back to those clean-looking lines."
    },
    {
      "type": "heading",
      "text": "When Straight Lines Cut Through Cities"
    },
    {
      "type": "paragraph",
      "text": "Occasionally a straight border slices through inhabited places, with surreal results. There are towns split down the middle by a surveyed line, libraries and houses that sit in two countries at once, and streets where one side of the road is in a different nation from the other. These oddities exist precisely because the border was defined by an abstract coordinate rather than by anything visible on the ground."
    },
    {
      "type": "heading",
      "text": "Reading Borders From Above"
    },
    {
      "type": "paragraph",
      "text": "Borders themselves are usually invisible from space — there is rarely a literal line painted across the desert. But you can often infer one. A sudden change in farming patterns, road networks, field shapes, or even vegetation along a perfectly straight edge is a strong hint that a political boundary runs there, because different countries manage land differently. Spotting that abrupt, ruler-straight change is a classic satellite-reading skill."
    }
  ]
};
